import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { observer } from 'mobx-react';
import { BridgedButton as Button } from 'components/common/bridged-button';
import styled from '@emotion/styled';
import { Loader } from 'components/common/loader';
import ErrorContainer from 'components/common/error-container';
import { useHasOrganizations } from 'hooks/use-has-organization.hook';
import { ONBOARDING_ROUTE_PREFIX } from 'components/routes/routes';
import useStores from 'hooks/use-stores.hooks';

export const LOGIN_PAGE_TITLE_ID = 'navigation.login';

const LoginPage: React.FC = () => {
  const {
    authStore: {
      show,
      logout,
      isAuthenticated,
      getRedirectToUrl,
      isCallbackPage,
      handleUniversalLoginCallback,
      hasAuth0RedirectErrorInUrl,
      handleAuth0RedirectErrorFromUrl
    }
  } = useStores();
  const { hasOrganizations, isLoadingOrganizations } = useHasOrganizations();
  const [auth0RedirectErrorMessage, setAuth0RedirectErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      if (hasAuth0RedirectErrorInUrl()) {
        setAuth0RedirectErrorMessage(handleAuth0RedirectErrorFromUrl());
        return;
      }
      if (isCallbackPage()) {
        handleUniversalLoginCallback();
      } else {
        show();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  if (!isAuthenticated && auth0RedirectErrorMessage) {
    return (
      <StyledErrorWrapper>
        <ErrorContainer message={auth0RedirectErrorMessage || ''} />
        <Button
          type="default"
          data-testid="back-to-login-button"
          onClick={() => {
            setAuth0RedirectErrorMessage(null);
            logout();
          }}>
          Back to login
        </Button>
      </StyledErrorWrapper>
    );
  }

  if (isAuthenticated) {
    if (isLoadingOrganizations) {
      return <Loader />;
    }

    return <Navigate replace to={hasOrganizations ? getRedirectToUrl() : `${ONBOARDING_ROUTE_PREFIX}/welcome`} />;
  }

  return null;
};

export default observer(LoginPage);

const StyledErrorWrapper = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 24px;
`;
